/**
 * Waits until an element has non-empty text content and is not marked as "skeleton" or in loading state.
 * Resolves immediately if it already has meaningful text.
 * @param {string | Element | null | undefined} target - A CSS selector string or a DOM Element for the target element.
 * If a selector string is provided, the function will query the document for the element.
 * @param {Object} [options={}] - Optional configuration object.
 * @param {number} [options.timeout] - Maximum time in milliseconds to wait before rejecting. Default is 8000 msecs.
 * @param {string[]} [options.invalidSelectors] - An array of CSS selectors. If the target element matches any of these,
 * it's considered "still loading" and the function will continue waiting. Default value is `[ '.skeleton' ]`.
 * @param {() => Element | null} [options.requery] - A callback to re-fetch the target element, useful if the element
 * may be replaced in the DOM (e.g. during re-rendering).
 * @returns {Promise<string>} A Promise that resolves with the element's text content once it's considered valid
 * (non-empty and not matching any `invalidSelectors`), or rejects on timeout.
 */
function waitForElementText (
	target: string | Element | null | undefined,
	options: {
        timeout?: number;
		invalidSelectors?: string[]; // selectors treated as "still loading"
		requery?: () => Element | null; // re-find the element if it was replaced
    } = {}
): Promise<string> {

	const { timeout = 8000, invalidSelectors = [ '.skeleton' ] } = options;

	const getEl = () => {

		if (typeof target === 'string') {

			return document.querySelector(target);

		}

		return target as Element | null | undefined;

	};

	let el = getEl();

	if (!el) {

		return Promise.reject(new Error(
			typeof target === 'string'
				? `Element not found for selector: ${target}`
				: 'Element is null or undefined'
		));

	}

	const isInvalid = (): boolean => invalidSelectors.some((sel) => {

		try {

			return el.matches(sel);

		} catch {

			// Ignore invalid selector syntax rather than breaking the wait
			return false;

		}

	});

	const hasValidText = (): boolean => {

		const text = el.textContent?.trim() ?? '';
		return !isInvalid() && text.length > 0;

	};

	return new Promise((resolve, reject) => {

		if (timeout === 0) {

			if (hasValidText()) {

				resolve((el.textContent ?? '').trim());

			} else {

				reject(new Error('Timeout waiting for element text'));

			}
			return;

		}

		if (hasValidText()) {

			resolve((el.textContent ?? '').trim());
			return;

		}

		const observer = new MutationObserver(() => {

			if (hasValidText()) {

				const text = (el.textContent ?? '').trim();
				observer.disconnect();
				resolve(text);

			}

		});

		observer.observe(el, {
			characterData: true,
			childList: true,
			subtree: true,
			attributes: true
		});

		setTimeout(() => {

			observer.disconnect();

			if (el && !el.isConnected && typeof options.requery === 'function') {

				const fresh = options.requery();

				if (fresh) {

					el = fresh;

				}

			}

			if (el && hasValidText()) {

				resolve((el.textContent ?? '').trim());

			} else {

				reject(new Error('Timeout waiting for element text'));

			}

		}, timeout);

	});

}

export default waitForElementText;
